import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Banknote, Bell, ChefHat, CreditCard, Minus, Plus, Printer, Smartphone, Wallet, X } from "lucide-react";
import { API, cartLineKey, describeChoice, fmtErr, money } from "../lib/pos";
import { printBill, printOrderKOTs, stationKotsForOrder } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import { ItemConfigurator } from "../components/ItemConfigurator";
import { SplitModal } from "../components/SplitModal";
import CashTenderModal from "../components/CashTenderModal";
import { ManagerPinModal } from "../components/ManagerPinModal";

/* ---------- New-order notifier for station users ---------- */
// Telephone-ring pattern — two short rings of ~0.85 s each with a ~0.3 s pause,
// total playtime ~3 s (matches the user's spec: "along with a telephone ring sound
// for 3 seconds"). Uses two mixed sine tones (~480 & 620 Hz) with a fast tremolo
// so it actually sounds like a POTS phone rather than a game beep.
function ring() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    // Resume in case autoplay policy suspended it — safe no-op if already running.
    if (ctx.state === "suspended") { try { ctx.resume(); } catch (_) { /* ignore */ } }

    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.28;
    master.connect(ctx.destination);

    // ring bursts: [start, duration] in seconds — two rings totalling ~3 s
    const bursts = [[0.00, 0.85], [1.15, 0.85], [2.30, 0.70]];

    bursts.forEach(([t0, dur]) => {
      // Two sine oscillators mixed for the classic dual-tone phone-ring timbre.
      const oscA = ctx.createOscillator();
      const oscB = ctx.createOscillator();
      oscA.type = "sine"; oscB.type = "sine";
      oscA.frequency.value = 480;
      oscB.frequency.value = 620;

      // 20 Hz tremolo on the gain to simulate the classic "brring" warble.
      const g = ctx.createGain();
      const trem = ctx.createOscillator();
      const tremGain = ctx.createGain();
      trem.type = "sine"; trem.frequency.value = 20;
      tremGain.gain.value = 0.35;
      trem.connect(tremGain).connect(g.gain);
      g.gain.value = 0.6;

      // ADSR envelope for this burst
      g.gain.setValueAtTime(0.0001, now + t0);
      g.gain.exponentialRampToValueAtTime(0.9, now + t0 + 0.04);
      g.gain.setValueAtTime(0.9, now + t0 + dur - 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t0 + dur);

      oscA.connect(g); oscB.connect(g); g.connect(master);
      oscA.start(now + t0); oscB.start(now + t0);
      trem.start(now + t0);
      oscA.stop(now + t0 + dur); oscB.stop(now + t0 + dur);
      trem.stop(now + t0 + dur);
    });

    // Tear down the context once all bursts finish.
    setTimeout(() => { try { ctx.close(); } catch (_) { /* ignore */ } }, 3200);
  } catch (_) { /* browser refused audio — ignore */ }
}

function useStationNewOrderNotifier(orders, station) {
  const knownIdsRef = useRef(null);         // Set<string> — seeded on first render
  const [toasts, setToasts] = useState([]); // [{id, order}]

  // Request browser-notification permission once for station users (best-effort).
  useEffect(() => {
    if (!station) return;
    try {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch (_) { /* ignore */ }
  }, [station]);

  useEffect(() => {
    if (!station) return;
    // First render: just seed known IDs and don't notify — every existing ticket
    // would otherwise fire a toast on page load.
    if (knownIdsRef.current === null) {
      knownIdsRef.current = new Set(orders.map((o) => o.id));
      return;
    }
    const known = knownIdsRef.current;
    const fresh = orders.filter((o) => !known.has(o.id));
    if (!fresh.length) return;
    fresh.forEach((o) => known.add(o.id));

    ring();
    fresh.forEach((o) => {
      const itemLines = (o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(", ");
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(`New order · Token ${o.token_number || o.order_number || ""}`, {
            body: itemLines || "Tap Prepare to start",
            tag: `qoh-order-${o.id}`,
          });
        }
      } catch (_) { /* ignore */ }
      const tid = `${o.id}_${Date.now()}`;
      setToasts((prev) => [...prev, { id: tid, order: o }]);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== tid)), 15000);
    });
  }, [orders, station]);

  const dismiss = (tid) => setToasts((prev) => prev.filter((t) => t.id !== tid));
  return { toasts, dismiss };
}

function NewOrderToasts({ toasts, dismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="new-order-toast-stack" data-testid="new-order-toast-stack">
      {toasts.map(({ id, order }) => (
        <div key={id} className="new-order-toast" data-testid="new-order-toast">
          <div className="new-order-toast-head">
            <Bell size={14} />
            <b>New order</b>
            <span className="new-order-toast-token">Token {order.token_number || order.order_number}</span>
            <button className="new-order-toast-close" onClick={() => dismiss(id)} data-testid="new-order-toast-close">
              <X size={12} />
            </button>
          </div>
          <ul className="new-order-toast-items">
            {(order.items || []).slice(0, 5).map((it, i) => (
              <li key={i}><b>{it.quantity}×</b> {it.name}</li>
            ))}
            {(order.items || []).length > 5 && <li>+{order.items.length - 5} more</li>}
          </ul>
        </div>
      ))}
    </div>
  );
}


export function Kitchen({ orders, stations, menu, billSettings, refresh }) {
  const { user } = useAuth();
  const isManager = user.role === "manager";
  const isCounterStaff = user.role === "manager" || user.role === "cashier";  // cashier gets the same kitchen-display controls as manager
  const isStation = user.role === "station";

  // New-order alert (station users only) — plays a beep, browser notification and
  // an in-app toast whenever a fresh ticket arrives that includes items routed to
  // this station.
  const myStation = useMemo(
    () => (isStation ? stations.find((s) => s.id === user.station_id) : null),
    [isStation, stations, user.station_id],
  );
  const relevantOrders = useMemo(() => {
    if (!isStation || !myStation) return orders;
    const cats = myStation.assigned_categories || [];
    if (!cats.length) return orders;
    return orders.filter((o) => (o.items || []).some((i) => cats.includes(i.category)));
  }, [orders, isStation, myStation]);
  const { toasts, dismiss } = useStationNewOrderNotifier(relevantOrders, isStation ? myStation : null);

  // Kitchen display refreshes every 5s for every role — newly-arrived tickets, status
  // changes from other cashiers and station "ready" flips surface without a manual reload.
  useEffect(() => {
    const t = setInterval(() => { refresh(); }, 5000);
    return () => clearInterval(t);
  }, [refresh]);
  const [filter, setFilter] = useState("all"); // manager: 'all' or station.id
  const [payingOrder, setPayingOrder] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [splitOrder, setSplitOrder] = useState(null);
  const [cashOrder, setCashOrder] = useState(null); // cash-tender step
  const [editingOrder, setEditingOrder] = useState(null);
  const [cancelPrompt, setCancelPrompt] = useState(null); // {order, itemIndex|null, defaultQty}
  const [pinAsk, setPinAsk] = useState(null); // { percent } while asking
  const active = orders.filter((o) => o.status !== "served" && o.status !== "cancelled");
  const filteredForManager = isCounterStaff && filter !== "all"
    ? active.filter((o) => {
        const st = stations.find((s) => s.id === filter);
        if (!st) return false;
        if (!st.assigned_categories.length) return true;
        return o.items.some((i) => st.assigned_categories.includes(i.category));
      }).map((o) => {
        const st = stations.find((s) => s.id === filter);
        if (!st || !st.assigned_categories.length) return o;
        return { ...o, items: o.items.filter((i) => st.assigned_categories.includes(i.category)) };
      })
    : active;

  const markReady = async (o) => {
    try {
      if (isCounterStaff) await axios.patch(`${API}/orders/${o.id}?status=ready`);
      else await axios.post(`${API}/orders/${o.id}/station-ready`);
      refresh();
    } catch (e) { alert(fmtErr(e)); }
  };
  const markPickedUp = async (o) => {
    try {
      await axios.post(`${API}/orders/${o.id}/station-picked-up`);
      refresh();
    } catch (e) { alert(fmtErr(e)); }
  };
  const startPrep = async (o) => {
    if (!isCounterStaff) return markReady(o); // stations: single tap flow
    try { await axios.patch(`${API}/orders/${o.id}?status=preparing`); refresh(); } catch (e) { alert(fmtErr(e)); }
  };
  const serve = async (o) => {
    try { await axios.post(`${API}/orders/${o.id}/serve`); refresh(); }
    catch (e) { alert(fmtErr(e)); }
  };
  const [kotPicker, setKotPicker] = useState(null); // { order, groups: [{station, items}] } — for station-picker reprint
  const pay = async (order, method) => {
    try {
      const r = await axios.post(`${API}/payments`, { order_id: order.id, amount: order.total, method });
      // Postpaid flow: KOTs already fired at order placement; only print the bill on payment.
      const fresh = (r.data && r.data.order) || { ...order, payment_method: method, status: "paid" };
      printBill(fresh, stations || [], { settings: billSettings, isDuplicate: false });
      setPayingOrder(null); refresh();
    } catch (e) { alert(fmtErr(e)); }
  };
  const paySplit = async (order, splits) => {
    try {
      const r = await axios.post(`${API}/payments`, { order_id: order.id, amount: order.total, method: "split", splits });
      const fresh = (r.data && r.data.order) || { ...order, payment_method: "split", payment_splits: splits, status: "paid" };
      printBill(fresh, stations || [], { settings: billSettings, isDuplicate: false });
      setSplitOrder(null); setPayingOrder(null); refresh();
    } catch (e) { alert(fmtErr(e)); }
  };

  const doReprintKOT = (o) => {
    // Show a station picker so the operator can pick exactly which station's KOT to reprint,
    // avoiding unwanted full-order reprints across every station. Every reprint is stamped 'DUPLICATE KOT'.
    const groups = stationKotsForOrder(o, stations || [], { excludeExpo: true });
    if (groups.length === 0) return;
    if (groups.length === 1) {
      printOrderKOTs(o, stations || [], { stationIds: [groups[0].station.id], duplicate: true });
      return;
    }
    setKotPicker({ order: o, groups });
  };
  const doReprintBill = (o) => printBill(o, stations || [], { settings: billSettings, isDuplicate: true });
  const applyKitchenDiscount = async (p) => {
    const map = { 0: "", 5: "5% - Known Customer", 10: "10% - Special Customer", 15: "15% - Very Special Customer", 20: "20% - VIP", 30: "30% - Staff", 100: "100% - Owner" };
    setApplyingDiscount(true);
    try {
      const r = await axios.patch(`${API}/orders/${payingOrder.id}/discount`, { discount_percent: p, discount_reason: map[p] || null });
      setPayingOrder(r.data);
      refresh();
    } catch (err) { alert(fmtErr(err)); }
    finally { setApplyingDiscount(false); }
  };
  const submitCancel = async ({ order, itemIndex, quantity, reason }) => {
    try {
      if (itemIndex == null) {
        const r = await axios.post(`${API}/orders/${order.id}/cancel`, { reason });
        printOrderKOTs(r.data.order, stations || [], { cancelled: true });
      } else {
        const r = await axios.post(`${API}/orders/${order.id}/cancel-item`, {
          item_index: itemIndex, quantity, reason,
        });
        const it = order.items[itemIndex];
        printOrderKOTs(r.data.order, stations || [], {
          cancelled: true,
          cancelledItems: [{ name: it.name, category: it.category, quantity, variant: it.variant, addons: it.addons, note: it.note }],
        });
      }
      setCancelPrompt(null);
      refresh();
    } catch (e) { alert(fmtErr(e)); }
  };

  const stationLabel = isCounterStaff
    ? (filter === "all" ? "All stations" : stations.find((s) => s.id === filter)?.name)
    : user.name;

  return (
    <div className="workspace">
      <NewOrderToasts toasts={toasts} dismiss={dismiss} />
      <div className="page-head">
        <div>
          <p className="eyebrow">LIVE KITCHEN CONTROL</p>
          <h1>{isCounterStaff ? "Kitchen display" : `${user.name} station`}</h1>
          <p className="muted">Viewing <b style={{ color: "#fff" }}>{stationLabel}</b> · {filteredForManager.length} active</p>
        </div>
        <span className="live"><i />Service live</span>
      </div>

      {isCounterStaff && (
        <div className="station-tabs">
          <button data-testid="station-filter-all-button" className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>All stations</button>
          {stations.filter((s) => s.active !== false).map((s) => (
            <button data-testid={`station-filter-${s.id}-button`} key={s.id} className={filter === s.id ? "active" : ""} onClick={() => setFilter(s.id)}>{s.name}</button>
          ))}
        </div>
      )}

      <div className="kitchen-grid">
        {filteredForManager.length ? filteredForManager.map((o) => {
          const totalQty = (o._all_items_count ?? o.items.reduce((s, i) => s + i.quantity, 0));
          const created = new Date(o.created_at);
          const mins = Math.max(0, Math.round((Date.now() - created.getTime()) / 60000));
          const isTakeaway = o.order_type === "takeaway";
          return (
            <div className={`ticket-card ${o.status} ${isTakeaway ? "ticket-takeaway" : "ticket-dinein"}`} key={o.id} data-testid={`ticket-card-${o.id}`}>
              <div className="ticket-meta">
                <b data-testid={`ticket-header-${o.id}`}>{o.table ? `TABLE ${o.table}` : (o.order_type === "table" ? "DINE-IN" : "TAKEAWAY")}</b>
                {o.order_number != null && <span className="on-badge" data-testid={`ticket-order-${o.id}`}>#{String(o.order_number).padStart(4, "0")}</span>}
                {o.token_number != null && <span className="tk-badge" data-testid={`ticket-token-${o.id}`}>T{String(o.token_number).padStart(2, "0")}</span>}
                <span>{mins}m</span>
              </div>
              {isCounterStaff && o.total != null && (
                <div className="ticket-total" data-testid={`ticket-total-${o.id}`}>
                  <span>ORDER TOTAL</span>
                  <b>{money(o.total)}</b>
                </div>
              )}
              {isCounterStaff && (() => {
                // Compact per-station pickup strip: shows every station involved with this
                // order and whether they're pending / ready / picked-up. Gives the counter
                // a single glance to know whether the guest has physically walked away yet.
                const sr = o.station_ready || {};
                const sp = o.station_picked_up || {};
                const cats = new Set((o.items || []).map(i => i.category));
                const involved = (stations || []).filter(s => (s.assigned_categories || []).some(c => cats.has(c)));
                if (!involved.length) return null;
                return (
                  <div className="pickup-strip" data-testid={`pickup-strip-${o.id}`} style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                    {involved.map(s => {
                      const state = sp[s.id] ? "picked" : sr[s.id] ? "ready" : "pending";
                      const style = state === "picked"
                        ? { background: "rgba(120,120,120,0.18)", color: "var(--muted)", borderColor: "rgba(120,120,120,0.35)" }
                        : state === "ready"
                          ? { background: "rgba(63,169,120,0.18)", color: "var(--green)", borderColor: "rgba(63,169,120,0.4)" }
                          : { background: "rgba(233,196,106,0.12)", color: "#e9c46a", borderColor: "rgba(233,196,106,0.35)" };
                      return (
                        <span key={s.id} className="pickup-chip" data-testid={`pickup-chip-${o.id}-${s.id}`}
                          style={{ ...style, padding: "3px 8px", borderRadius: 3, border: "1px solid", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>
                          {state === "picked" ? "✓" : state === "ready" ? "•" : "◦"} {s.name}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
              <h3>{totalQty} items <small>· {(() => {
                if (!isCounterStaff && (o.station_ready || {})[user.station_id]) return "my portion ready";
                return o.status;
              })()}</small></h3>
              {o.items.map((i, ix) => {
                const choice = describeChoice(i.variant, i.addons);
                const eff = i.quantity - (i.cancelled_quantity || 0);
                const canCancelLine = isCounterStaff && eff > 0 && !cancelPrompt;
                return (
                  <div className={`k-item ${eff <= 0 ? "k-item-void" : ""}`} key={ix}>
                    <b>{eff}×{(i.cancelled_quantity || 0) > 0 && <small className="k-void-tag"> (−{i.cancelled_quantity} voided)</small>}</b>
                    <span>
                      {i.name}
                      {choice && <small className="k-choice"> ({choice})</small>}
                      {i.note && <small className="k-note" data-testid={`ticket-note-${o.id}-${ix}`}>📝 {i.note}</small>}
                    </span>
                    <em>{i.category}</em>
                    {canCancelLine && (
                      <button
                        data-testid={`ticket-cancel-line-${o.id}-${ix}`}
                        className="k-line-void"
                        title="Cancel this line"
                        onClick={() => setCancelPrompt({ order: o, itemIndex: ix, defaultQty: eff })}
                      >✕</button>
                    )}
                  </div>
                );
              })}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {isCounterStaff && o.status === "sent" && (
                  <button data-testid={`order-${o.id}-start-button`} className="primary wide" onClick={() => startPrep(o)}>Start cooking</button>
                )}
                {isCounterStaff
                  ? (o.status === "sent" || o.status === "preparing") && (
                    <button data-testid={`order-${o.id}-ready-button`} className="primary wide" onClick={() => markReady(o)}>Mark ready</button>
                  )
                  : (() => {
                      const doneForMe = (o.station_ready || {})[user.station_id];
                      const pickedUp = (o.station_picked_up || {})[user.station_id];
                      const isDone = doneForMe || o.status === "ready" || o.status === "paid" || o.status === "served";
                      if (doneForMe && !pickedUp) {
                        return (
                          <>
                            <div className="ready-chip" data-testid={`order-${o.id}-ready-chip`} style={{ padding: "8px 12px", background: "rgba(63,169,120,0.15)", border: "1px solid rgba(63,169,120,0.4)", color: "var(--green)", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700, textAlign: "center", borderRadius: 4 }}>✓ My portion ready</div>
                            <button
                              data-testid={`order-${o.id}-picked-up-button`}
                              className="primary wide"
                              onClick={() => markPickedUp(o)}
                              title="Guest has collected their food from this station — hide from customer display"
                            >Items picked up</button>
                          </>
                        );
                      }
                      if (pickedUp) {
                        return (
                          <div data-testid={`order-${o.id}-picked-up-chip`} style={{ padding: "8px 12px", background: "rgba(120,120,120,0.15)", border: "1px solid rgba(120,120,120,0.35)", color: "var(--muted)", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700, textAlign: "center", borderRadius: 4 }}>✓ Picked up</div>
                        );
                      }
                      return (
                        <button
                          data-testid={`order-${o.id}-ready-button`}
                          className="primary wide"
                          onClick={() => markReady(o)}
                          disabled={!!isDone}
                          title={isDone ? "Your portion is already marked ready" : "Mark my items ready"}
                        >{isDone ? "✓ My portion ready" : "Mark my items ready"}</button>
                      );
                    })()
                }
                {isCounterStaff && (o.status === "sent" || o.status === "preparing" || o.status === "ready") && !o.paid_at && (
                  <button data-testid={`order-${o.id}-edit-button`} className="pay-btn" onClick={() => setEditingOrder(o)}>Add items</button>
                )}
                {isCounterStaff && o.status === "ready" && !o.paid_at && (
                  <button data-testid={`order-${o.id}-collect-button`} className="primary wide" onClick={() => setPayingOrder(o)}>Collect payment · {money(o.total)}</button>
                )}
                {isCounterStaff && (o.status === "paid" || (o.status === "ready" && o.paid_at)) && (
                  <button data-testid={`order-${o.id}-serve-button`} className="primary wide serve-btn" onClick={() => serve(o)}>
                    {o.token_number != null
                      ? `Mark served · release T${String(o.token_number).padStart(2, "0")}`
                      : "Mark served"}
                  </button>
                )}
                {isCounterStaff && (
                  <button data-testid={`order-${o.id}-reprint-kot-button`} className="pay-btn ghost" onClick={() => doReprintKOT(o)}>
                    <Printer size={14} />Reprint KOT
                  </button>
                )}
                {isCounterStaff && (o.status === "paid" || o.paid_at) && (
                  <button data-testid={`order-${o.id}-reprint-bill-button`} className="pay-btn ghost" onClick={() => doReprintBill(o)}>
                    <Printer size={14} />Reprint Bill
                  </button>
                )}
                {isCounterStaff && o.status !== "served" && (
                  <button data-testid={`order-${o.id}-cancel-order-button`} className="pay-btn danger" onClick={() => setCancelPrompt({ order: o, itemIndex: null })}>
                    Cancel order
                  </button>
                )}
              </div>
            </div>
          );
        }) : (
          <div className="empty wide-empty">
            <ChefHat size={34} />
            <b>All clear</b>
            <span>New tickets will appear here automatically</span>
          </div>
        )}
      </div>

      {payingOrder && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setPayingOrder(null)}>
          <div className="modal" style={{ width: "min(460px, 92vw)" }}>
            <div className="section-line">
              <h2>Collect payment</h2>
              <button data-testid="collect-close-button" onClick={() => setPayingOrder(null)}>×</button>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 4px" }}>
              {payingOrder.table ? `Table ${payingOrder.table}` : (payingOrder.order_type === "table" ? "Dine-in" : "Takeaway")} · Token T{String(payingOrder.token_number || "").padStart(2, "0")}
            </p>

            <div className="discount-block" data-testid="kitchen-discount-block" style={{ marginTop: 2 }}>
              <label className="eyebrow" style={{ display: "block", marginBottom: 4 }}>DISCOUNT SLAB <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· recalculates GST + total</span></label>
              <select
                data-testid="kitchen-discount-select"
                className="filter-select"
                style={{ width: "100%" }}
                value={payingOrder.discount_percent || 0}
                disabled={applyingDiscount}
                onChange={(e) => {
                  const p = Number(e.target.value);
                  if (p === (payingOrder.discount_percent || 0)) return;
                  // Gate 30% Staff / 100% Owner behind a 4-digit manager PIN.
                  if (p >= 30) {
                    setPinAsk({ percent: p });
                    return;
                  }
                  applyKitchenDiscount(p);
                }}
              >
                <option value={0}>No discount</option>
                <option value={5}>5% — Known Customer</option>
                <option value={10}>10% — Special Customer</option>
                <option value={15}>15% — Very Special Customer</option>
                <option value={20}>20% — VIP</option>
                <option value={30}>30% — Staff</option>
                <option value={100}>100% — Owner</option>
              </select>
              {(payingOrder.discount_percent || 0) > 0 && (
                <p className="muted" data-testid="kitchen-discount-preview" style={{ fontSize: 11, marginTop: 6 }}>
                  Applying <b style={{ color: "#fff" }}>{payingOrder.discount_percent}%</b> · saving <b style={{ color: "var(--red)" }}>-{money(payingOrder.discount || 0)}</b> on {money(payingOrder.subtotal || 0)}
                </p>
              )}
            </div>

            <div className="collect-total" style={{ marginTop: 6 }}><span>AMOUNT DUE</span><b data-testid="kitchen-amount-due">{money(payingOrder.total)}</b></div>
            <div className="pay-row">
              <button data-testid={`collect-cash-${payingOrder.id}`} className="pay-btn" disabled={applyingDiscount} onClick={() => { setCashOrder(payingOrder); setPayingOrder(null); }}><Banknote size={16} />Cash</button>
              <button data-testid={`collect-card-${payingOrder.id}`} className="pay-btn" disabled={applyingDiscount} onClick={() => pay(payingOrder, "card")}><CreditCard size={16} />Card</button>
              <button data-testid={`collect-upi-${payingOrder.id}`} className="pay-btn" disabled={applyingDiscount} onClick={() => pay(payingOrder, "upi")}><Smartphone size={16} />UPI</button>
              <button data-testid={`collect-split-${payingOrder.id}`} className="pay-btn" disabled={applyingDiscount} onClick={() => { setSplitOrder(payingOrder); }}><Wallet size={16} />Split</button>
            </div>
          </div>
        </div>
      )}

      {splitOrder && (
        <SplitModal
          total={splitOrder.total}
          onCancel={() => setSplitOrder(null)}
          onSubmit={(splits) => paySplit(splitOrder, splits)}
        />
      )}

      {cashOrder && (
        <CashTenderModal
          total={cashOrder.total}
          onCancel={() => setCashOrder(null)}
          onDone={async () => { const o = cashOrder; setCashOrder(null); await pay(o, "cash"); }}
        />
      )}

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          menu={menu}
          stations={stations || []}
          onCancel={() => setEditingOrder(null)}
          onSaved={() => { setEditingOrder(null); refresh(); }}
        />
      )}

      {kotPicker && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setKotPicker(null)}>
          <div className="modal" style={{ width: "min(420px, 92vw)" }} data-testid="kot-picker-modal">
            <div className="section-line">
              <h2>Reprint KOT</h2>
              <button data-testid="kot-picker-close-button" onClick={() => setKotPicker(null)}>×</button>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
              Order #{kotPicker.order.order_number != null ? String(kotPicker.order.order_number).padStart(4, "0") : kotPicker.order.id.slice(-6)} · pick a station to reprint that KOT.
            </p>
            <div className="kot-picker-list" data-testid="kot-picker-list">
              {kotPicker.groups.map(({ station, items }) => {
                const totalQty = items.reduce((s, i) => s + Math.max(0, (i.quantity || 0) - (i.cancelled_quantity || 0)), 0);
                return (
                  <button
                    key={station.id}
                    data-testid={`kot-picker-station-${station.id}-button`}
                    className="kot-picker-row"
                    onClick={() => {
                      printOrderKOTs(kotPicker.order, stations || [], { stationIds: [station.id], duplicate: true });
                      setKotPicker(null);
                    }}
                  >
                    <div>
                      <b>{station.name}</b>
                      <small>{items.length} item{items.length > 1 ? "s" : ""} · {totalQty} qty</small>
                    </div>
                    <Printer size={16} />
                  </button>
                );
              })}
            </div>
            {kotPicker.groups.length > 1 && (
              <button
                data-testid="kot-picker-all-button"
                className="outline wide"
                style={{ marginTop: 12 }}
                onClick={() => {
                  printOrderKOTs(kotPicker.order, stations || [], { duplicate: true });
                  setKotPicker(null);
                }}
              >
                Reprint all stations
              </button>
            )}
          </div>
        </div>
      )}

      {cancelPrompt && (
        <CancelReasonModal
          order={cancelPrompt.order}
          itemIndex={cancelPrompt.itemIndex}
          defaultQty={cancelPrompt.defaultQty}
          onCancel={() => setCancelPrompt(null)}
          onSubmit={submitCancel}
        />
      )}

      <ManagerPinModal
        open={!!pinAsk}
        percent={pinAsk?.percent}
        onCancel={() => setPinAsk(null)}
        onVerified={async () => {
          const p = pinAsk.percent;
          setPinAsk(null);
          await applyKitchenDiscount(p);
        }}
      />
    </div>
  );
}

/* ---------- Cancel Reason modal (item or whole order) ---------- */
function CancelReasonModal({ order, itemIndex, defaultQty, onCancel, onSubmit }) {
  const isItem = itemIndex != null;
  const item = isItem ? order.items[itemIndex] : null;
  const [reason, setReason] = useState("");
  const [qty, setQty] = useState(defaultQty || 1);
  const [busy, setBusy] = useState(false);
  const suggestions = ["Guest changed mind", "Wrong item sent", "Out of stock", "Kitchen error", "Duplicate order", "Comp"];
  const canSubmit = reason.trim().length >= 2 && (!isItem || (qty >= 1 && qty <= (defaultQty || 1))) && !busy;
  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onCancel()}>
      <div className="modal" style={{ width: "min(440px, 92vw)" }} data-testid="cancel-modal">
        <div className="section-line">
          <h2>{isItem ? "Cancel line" : "Cancel whole order"}</h2>
          <button data-testid="cancel-close-button" onClick={onCancel}>×</button>
        </div>
        <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>
          {order.table ? `Table ${order.table}` : (order.order_type === "table" ? "Dine-in" : "Takeaway")} · Token T{String(order.token_number || "").padStart(2, "0")}
          {isItem && item && ` · ${item.name}`}
        </p>
        {isItem && (
          <label className="field" style={{ marginBottom: 10 }}>
            <span>QUANTITY TO VOID (max {defaultQty})</span>
            <input
              data-testid="cancel-quantity-input"
              type="number"
              min="1"
              max={defaultQty}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(defaultQty || 1, Number(e.target.value) || 1)))}
            />
          </label>
        )}
        <label className="field">
          <span>REASON (required, will be logged)</span>
          <input
            data-testid="cancel-reason-input"
            type="text"
            placeholder="e.g. Guest changed mind"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </label>
        <div className="discounts" style={{ marginTop: 8 }}>
          {suggestions.map((s) => (
            <button key={s} data-testid={`cancel-suggestion-${s.replace(/\s+/g, "-").toLowerCase()}`} className={reason === s ? "selected" : ""} onClick={() => setReason(s)}>
              {s}
            </button>
          ))}
        </div>
        <button
          data-testid="cancel-confirm-button"
          className="primary wide"
          style={{ marginTop: 14 }}
          disabled={!canSubmit}
          onClick={async () => {
            setBusy(true);
            try {
              await onSubmit({ order, itemIndex, quantity: qty, reason: reason.trim() });
            } finally { setBusy(false); }
          }}
        >
          {busy ? "Cancelling…" : (isItem ? `Cancel ${qty}× ${item?.name || "line"}` : "Cancel whole order")}
        </button>
        <p className="muted" style={{ fontSize: 11, marginTop: 10 }}>
          A duplicate KOT marked <b>CANCELLED</b> will print automatically for the impacted station(s).
        </p>
      </div>
    </div>
  );
}

/* ---------- Edit Order (add items) modal ---------- */
function EditOrderModal({ order, menu, stations, onCancel, onSaved }) {
  const [added, setAdded] = useState([]); // cart-style
  const [search, setSearch] = useState("");
  const [configuring, setConfiguring] = useState(null);
  const [busy, setBusy] = useState(false);

  const q = search.trim().toLowerCase();
  const enabledMenu = menu.filter((x) => x.effective_active !== false);
  const results = q
    ? enabledMenu.filter((x) => x.name.toLowerCase().includes(q) || (x.description || "").toLowerCase().includes(q) || x.category.toLowerCase().includes(q)).slice(0, 12)
    : enabledMenu.slice(0, 12);

  const addLine = (item, variant, addons, unitPrice, quantity = 1, note = null) => {
    const key = cartLineKey(item.id, variant, addons);
    setAdded((c) => {
      const ex = c.find((x) => x.cartKey === key);
      if (ex) {
        return c.map((x) => (x.cartKey === key
          ? { ...x, quantity: x.quantity + quantity, note: note || x.note || null }
          : x));
      }
      return [...c, {
        cartKey: key,
        menu_id: item.id,
        name: item.name,
        category: item.category,
        price: unitPrice,
        quantity: quantity,
        variant: variant ? { id: variant.id, name: variant.name, price_delta: variant.price_delta || 0, sub_variants: (variant.sub_variants || []).map((sv) => ({ id: sv.id, name: sv.name, price_delta: sv.price_delta || 0 })) } : null,
        addons: addons || [],
        note: note || null,
      }];
    });
  };
  const openItem = (item) => {
    // All items now open configurator (quantity + notes required as UX standard)
    setConfiguring(item);
  };
  const bumpLine = (key, delta) => setAdded((c) => c.flatMap((x) => {
    if (x.cartKey !== key) return [x];
    const q = x.quantity + delta;
    return q > 0 ? [{ ...x, quantity: q }] : [];
  }));

  const save = async () => {
    if (!added.length) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orders/${order.id}/items`, {
        items: added.map((x) => ({
          menu_id: x.menu_id, name: x.name, price: x.price, quantity: x.quantity, category: x.category,
          variant: x.variant, addons: x.addons, note: x.note || null,
        })),
      });
      // Auto-print a supplementary KOT for the newly-added items only
      const updated = r.data || {};
      const addedItems = updated._added_items || added.map((x) => ({
        name: x.name, category: x.category, quantity: x.quantity, variant: x.variant, addons: x.addons, note: x.note || null,
      }));
      try { printOrderKOTs(updated.id ? updated : { ...order, ...updated }, stations || [], { addedItems }); }
      catch (_) { /* print best-effort */ }
      onSaved();
    } catch (e) { alert(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const addedSubtotal = added.reduce((a, x) => a + x.price * x.quantity, 0);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onCancel()}>
      <div className="modal builder" style={{ width: "min(720px, 94vw)", maxHeight: "88vh", overflowY: "auto" }}>
        <div className="section-line">
          <h2>Add items to Token T{String(order.token_number || "").padStart(2, "0")}</h2>
          <button data-testid="edit-close-button" onClick={onCancel}>×</button>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          {order.table ? `Table ${order.table}` : (order.order_type === "table" ? "Dine-in" : "Takeaway")} · currently {order.items.length} lines · new items ship to their stations immediately
        </p>

        <div className="menu-search" style={{ marginTop: 12 }}>
          <input
            data-testid="edit-search-input"
            type="search"
            placeholder="Search menu to add…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="search-clear" onClick={() => setSearch("")}>×</button>}
        </div>

        <div className="edit-menu-grid">
          {results.map((item) => {
            const hasChoices = (item.variants?.length || 0) > 0 || (item.addons?.length || 0) > 0;
            return (
              <button data-testid={`edit-add-${item.id}-button`} className="menu-item edit-mini" key={item.id} onClick={() => openItem(item)}>
                <div>
                  <b>{item.name}{hasChoices && <span className="mi-flag">customisable</span>}</b>
                  <strong>{money(item.price)} · {item.category}</strong>
                </div>
                <Plus size={14} />
              </button>
            );
          })}
        </div>

        <p className="eyebrow" style={{ marginTop: 14 }}>NEW ITEMS</p>
        {added.length ? (
          <div className="cart" style={{ maxHeight: 200, overflowY: "auto" }}>
            {added.map((x) => {
              const choice = describeChoice(x.variant, x.addons);
              return (
                <div className="cart-row" key={x.cartKey}>
                  <div>
                    <b>{x.name}</b>
                    <small>{money(x.price)} · {x.category}</small>
                    {choice && <em className="cart-choice">{choice}</em>}
                  </div>
                  <div className="stepper">
                    <button onClick={() => bumpLine(x.cartKey, -1)}><Minus size={13} /></button>
                    <span>{x.quantity}</span>
                    <button onClick={() => bumpLine(x.cartKey, +1)}><Plus size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>Search above and tap items to add.</p>
        )}
        <div className="collect-total" data-testid="edit-added-subtotal"><span>ADDED SUBTOTAL</span><b>{money(addedSubtotal)}</b></div>
        <button data-testid="edit-save-button" className="primary wide" disabled={!added.length || busy} onClick={save}>
          {busy ? "Adding…" : `Add ${added.length} line${added.length === 1 ? "" : "s"} to ticket`}
        </button>

        {configuring && (
          <ItemConfigurator
            item={configuring}
            onCancel={() => setConfiguring(null)}
            onAdd={({ variant, addons, unitPrice, quantity, note }) => { addLine(configuring, variant, addons, unitPrice, quantity, note); setConfiguring(null); }}
          />
        )}
      </div>
    </div>
  );
}

/* ---------- Manager overview ---------- */
